import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy } from '@angular/core';
import { User } from '../model/user';
import { getStoredUser, setStoredToken, setStoredUser } from '../helpers/user-storage.util';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, EMPTY, finalize, Subscription, switchMap, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { AccountSideNavComponent } from '../account-side-nav/account-side-nav.component';
import { ServiceCapabilitiesDialogComponent } from '../service-capabilities-dialog/service-capabilities-dialog.component';
import { MiscService } from '../services/misc.service';
import { ToastrService } from 'ngx-toastr';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { UserService } from '../services/user.service';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { DocumentService } from '../services/document.service';
import { WelcomeDialogComponent } from '../welcome-dialog/welcome-dialog.component';

// Shared by every Manage Account instance: a save sent from one that was since closed still blocks Verify in the next,
// so that save's late answer cannot overwrite a newer verification.
const profileSaves = { pending: 0, generation: 0 };

@Component({
  selector: 'app-manage-account',
  standalone: true,
  imports: [
    CommonModule,
    AccountSideNavComponent,
    ServiceCapabilitiesDialogComponent,
    FormsModule,
    MatSelectModule,
    MatIconModule,
    ReactiveFormsModule,
    WelcomeDialogComponent,
  ],
  templateUrl: './manage-account.component.html',
  styleUrl: './manage-account.component.scss',
})
export class ManageAccountComponent implements OnDestroy {
  private subscriptions = new Subscription();
  private verificationPending = false;
  private verificationCooldown = false;

  get addressVerificationEnabled(): boolean {
    return environment.addressVerificationEnabled;
  }

  get addressVerificationNeeded(): boolean {
    return getStoredUser()?.addressVerificationNeeded === true;
  }

  get verifyAddressDisabled(): boolean {
    return this.verificationPending || this.verificationCooldown || profileSaves.pending > 0;
  }

  tabState: string = 'rfp';
  activeTab: string = 'profile';

  // Reads the flag fresh from storage (not this.user) so it reflects the latest
  // persisted value — it flips to unlocked the moment a completing save writes
  // welcomeEmailPending:false back to localStorage, with no re-login.
  get isLocked(): boolean {
    return getStoredUser()?.welcomeEmailPending === true;
  }
  emailRegx: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  id: string = '';
  user: User = {};
  // profile data
  protected nameUpdate = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
  });

  protected emailUpdate = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(this.emailRegx),
    ]),
  });
  phone: string = '';
  phone2: string = '';
  phoneOs: string = '';
  phone2Os: string = '';
  streetAddress: string = '';
  city: string = '';
  state: string = '';
  zipcode: string = '';
  ccName1: string = '';
  ccName2: string = '';
  ccEmail1: string = '';
  ccEmail2: string = '';
  ccPhone1: string = '';
  ccPhone2: string = '';
  bio: string = '';
  timezone: any = {};

  // organization data
  orgName: string = '';
  orgEmail: string = '';
  orgPhone: string = '';
  orgStreetAddress: string = '';
  orgCity: string = '';
  orgState: string = '';
  orgDecodedState: string = '';
  orgZipcode: string = '';
  orgWebsite: string = '';
  orgDescription: string = '';

  currentConversation: string = '';

  // services and capabilities
  serviceCapabilitiesActive: boolean = false;
  serviceCapabilitiesType: string = '';
  stateCountyDetails: any = [];
  serviceZoneStateCountyDetails: any = [];
  serviceZoneCounties: any = [];
  timeZones: any = [];
  serviceTypes: any = [];
  toggleNewServiceZone: boolean = false;
  allNewZoneServiceTypesSelected: boolean = false;
  newZoneServiceTypes: any = [];
  selectedNewZoneState: any = '';
  selectedNewCounties: any = [];
  currentServiceZone: any = '';
  allUpdateZoneServiceTypesSelected: boolean = false;

  editNameToggle: boolean = false;
  editEmailToggle: boolean = false;
  editPhoneToggle: boolean = false;
  editAddressToggle: boolean = false;
  editCcInfoToggle: boolean = false;
  editProfileAdditionalInfoToggle: boolean = false;
  editOrgInfoToggle: boolean = false;
  editOrgAddressToggle: boolean = false;
  editOrgAdditionalInfoToggle: boolean = false;
  editZoneAndCapabilitiesToggle: boolean = false;
  editZoneToggle: boolean = false;
  updateSucess: boolean = false;

  openWelcomeDialog: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private elementRef: ElementRef<HTMLElement>,
    private miscService: MiscService,
    private toastr: ToastrService,
    private userService: UserService,
    private utilityService: CommonUtilityServiceService,
    private documentService: DocumentService,
  ) {}

  ngOnInit(): void {
    // set tab state from url
    this.route.queryParams.subscribe((params) => {
      this.activeTab = this.isLocked ? 'servicezones' : (params['tab'] || this.activeTab);
    });
    this.subscriptions.add(
      combineLatest([this.route.fragment, this.route.queryParams]).pipe(
        switchMap(([fragment]) => fragment === 'address' ? timer(0) : EMPTY),
      ).subscribe(() => {
        this.elementRef.nativeElement.querySelector('#address')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }),
    );
    this.user = getStoredUser();
    if (this.user?.newUser) {
      this.markUserOnboarded();
    }
    // this.openWelcomeDialog = this.user?.newUser || false;
    // turning off the welcome dialog, keeping it just in case we turn it back on
    this.openWelcomeDialog = false;
    this.miscService.fetchStateCountyDetails().subscribe((result: any) => {
      this.stateCountyDetails = result;
      const updatedStateCounties = result.map((stateCountyDetail: any) => {
        return {
          ...stateCountyDetail,
          counties: ['All Counties', ...stateCountyDetail.counties],
        };
      });
      this.serviceZoneStateCountyDetails = [
        ...updatedStateCounties,
        { state: 'NATIONWIDE', name: 'Nationwide', counties: [] },
      ];
    });

    this.miscService.fetchTimeZones().subscribe((result: any) => {
      this.timeZones = result;
    });

    this.miscService.fetchServiceTypes().subscribe((result: any) => {
      if (result) {
        this.serviceTypes = result.map((serviceType: any) => {
          const userSelectedServiceType = this.user?.selectedServiceTypes?.find(
            (ust: any) => ust.type === serviceType.type,
          );
          let serviceSubTypes = serviceType.serviceSubTypes;
          if (serviceSubTypes) {
            serviceSubTypes = serviceSubTypes
              .filter((sst: any) => sst.enabled)
              .map((sst: any) => {
                return {
                  ...sst,
                  checked:
                    userSelectedServiceType?.serviceSubTypes?.find(
                      (usst: any) => usst.type === sst.type,
                    )?.enabled || false,
                };
              });
          }
          return {
            ...serviceType,
            checked: userSelectedServiceType?.enabled || false,
            showSubTypes:
              userSelectedServiceType?.enabled && serviceSubTypes?.length > 0,
            serviceSubTypes: serviceSubTypes,
          };
        });
        this.newZoneServiceTypes = this.user?.selectedServiceTypes || [];
      }
    });
    if (this.user) {
      this.nameUpdate.patchValue({
        firstName: this.user.firstName,
        lastName: this.user.lastName,
      });
      this.emailUpdate.patchValue({
        email: this.user.email,
      });
      this.phone = this.user.phone ?? '';
      this.phoneOs = this.user.phoneOs ?? '';
      this.phone2 = this.user.phone2 ?? '';
      this.phone2Os = this.user.phone2Os ?? '';
      // populating the user's address
      const location = this.user.location;
      if (location) {
        this.streetAddress = location.street ?? '';
        this.city = location.city ?? '';
        if (location.state) {
          this.state = location.state;
        }
        this.zipcode = location.zipcode ?? '';
      }
      this.ccName1 = this.user.ccName1 ?? '';
      this.ccName2 = this.user.ccName2 ?? '';
      this.ccEmail1 = this.user.ccEmail1 ?? '';
      this.ccEmail2 = this.user.ccEmail2 ?? '';
      this.ccPhone1 = this.user.ccPhone1 ?? '';
      this.ccPhone2 = this.user.ccPhone2 ?? '';
      this.bio = this.user.bio ?? '';
      if (this.user.timezone) {
        this.timezone = this.user.timezone;
      }
      const organization = this.user.organization;
      if (organization) {
        this.orgName = organization.name;
        this.orgEmail = organization.email;
        this.orgPhone = organization.phone;
        const orgLocation = organization.location;
        if (orgLocation) {
          this.orgStreetAddress = orgLocation.street;
          this.orgCity = orgLocation.city;
          this.orgState = orgLocation.state;
          this.orgZipcode = orgLocation.zipcode;
        }
        this.orgWebsite = organization.website;
        this.orgDescription = organization.description;
      }
    }
  }

  decodeState(state: string): string {
    return this.utilityService.decodeUsState(state);
  }

  formatCounty(counties: any) {
    return counties?.join(', ');
  }

  formatServiceSubTypes(subTypes: any): string {
    let subtypesString = '';
    const filteredSubTypes = subTypes.filter((st: any) => st.enabledForZone);
    if (filteredSubTypes?.length > 0) {
      subtypesString =
        ' (' +
        filteredSubTypes.map((st: any) => st.displayName).join(', ') +
        ')';
    }
    return subtypesString;
  }

  filterServiceList(services: any) {
    return services?.filter((service: any) => service.enabledForZone);
  }

  isZoneDocumentRequired(services: any): boolean {
    return services?.some(
      (service: any) =>
        service.enabledForZone && service.vendorDocumentRequired,
    );
  }

  changeConversation(conversation: string) {
    this.currentConversation = conversation;
  }

  toggleTab(tab: string) {
    if (this.isLocked) { tab = 'servicezones'; }
    this.activeTab = tab;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      replaceUrl: true,
    });
  }

  toggleServiceCapabilitiesModal() {
    this.serviceCapabilitiesActive = !this.serviceCapabilitiesActive;
  }

  toggleWelcomeDialog() {
    this.openWelcomeDialog = !this.openWelcomeDialog;
    this.markUserOnboarded();
  }

  // Resets the new-user onboarding flag once: clears newUser and stamps the
  // first login date. Shared by the first-login redirect (ngOnInit) and the
  // welcome dialog so the reset logic lives in one place.
  private markUserOnboarded() {
    this.updateUser(
      {
        ...this.user,
        newUser: false,
        firstLogInDate: new Date(),
      },
      false,
    );
  }

  editName() {
    this.editNameToggle = !this.editNameToggle;
  }

  editEmail() {
    this.editEmailToggle = !this.editEmailToggle;
  }

  editPhone() {
    this.editPhoneToggle = !this.editPhoneToggle;
  }

  editCCInfo() {
    this.editCcInfoToggle = !this.editCcInfoToggle;
  }

  editAddress() {
    this.editAddressToggle = !this.editAddressToggle;
  }

  editProfileAddtionalInfo() {
    this.editProfileAdditionalInfoToggle =
      !this.editProfileAdditionalInfoToggle;
  }

  editOrgInfo() {
    this.editOrgInfoToggle = !this.editOrgInfoToggle;
  }

  editOrgAddress() {
    this.editOrgAddressToggle = !this.editOrgAddressToggle;
  }

  editOrgAdditionalInfo() {
    this.editOrgAdditionalInfoToggle = !this.editOrgAdditionalInfoToggle;
  }

  addNewServiceZone() {
    this.allNewZoneServiceTypesSelected = true;
    this.handleSelectAllNewZoneServiceTypes();
    this.toggleNewServiceZone = true;
  }

  expandSubType(service: any) {
    this.serviceTypes = this.serviceTypes.map((st: any) => {
      if (st.type === service.type) {
        return { ...st, showSubTypes: !service.showSubTypes };
      }
      return st;
    });
  }

  editServiceZone(serviceZone: any) {
    let currentStateCounty;
    if (serviceZone.nationwide) {
      currentStateCounty = this.serviceZoneStateCountyDetails.find(
        (stateCounty: any) => stateCounty.state === 'NATIONWIDE',
      );
    } else {
      currentStateCounty = this.serviceZoneStateCountyDetails.find(
        (stateCounty: any) => stateCounty.state === serviceZone.state,
      );
    }
    const pendingUpdates = {
      selectedState: currentStateCounty,
      selectedCounties: serviceZone.counties,
      selectedServices: serviceZone.services,
      zoneDocuments: serviceZone.zoneDocuments,
    };
    this.currentServiceZone = {
      ...pendingUpdates,
      state: serviceZone.nationwide ? 'NATIONWIDE' : serviceZone.state,
    };
    this.allUpdateZoneServiceTypesSelected =
      this.currentServiceZone?.selectedServices.every(
        (service: any) =>
          service.enabledForZone &&
          (!service.serviceSubTypes ||
            service.serviceSubTypes?.length === 0 ||
            service.serviceSubTypes?.every(
              (subType: any) => subType.enabledForZone,
            )),
      );
    this.editZoneToggle = !this.editZoneToggle;
  }

  saveName() {
    if (!this.nameUpdate.valid) {
      this.toastr.error('First name and last name are required.');
    } else if (
      this.nameUpdate.value.firstName !== this.user.firstName ||
      this.nameUpdate.value.lastName !== this.user.lastName
    ) {
      this.updateUser(
        {
          ...this.user,
          firstName: this.nameUpdate.value.firstName ?? '',
          lastName: this.nameUpdate.value.lastName ?? '',
        },
        true,
      );
      this.editNameToggle = !this.editNameToggle;
    } else {
      this.editNameToggle = !this.editNameToggle;
    }
  }

  savePhone() {
    if (
      this.phone !== this.user.phone ||
      this.phoneOs !== this.user.phoneOs ||
      this.phone2 !== this.user.phone2 ||
      this.phone2Os !== this.user.phone2Os
    ) {
      this.updateUser(
        {
          ...this.user,
          phone: this.phone,
          phoneOs: this.phoneOs,
          phone2: this.phone2,
          phone2Os: this.phone2Os,
        },
        true,
      );
    }
    this.editPhoneToggle = !this.editPhoneToggle;
  }

  saveEmail() {
    if (!this.emailUpdate.valid) {
      this.toastr.error('Please enter a valid email.');
    } else if (
      this.emailUpdate.value.email &&
      this.emailUpdate.value.email !== this.user.email
    ) {
      this.userService
        .updateUserEmail({
          ...this.user,
          email: this.emailUpdate.value.email.toLowerCase(),
        })
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            const token = response.token;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              setStoredToken(token);
              this.toastr.success(apiResponse.message);
              this.editEmailToggle = !this.editEmailToggle;
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    } else {
      this.editEmailToggle = !this.editEmailToggle;
    }
  }

  saveAddress() {
    const location = this.user.location;
    if (
      !location ||
      this.streetAddress !== location?.street ||
      this.city !== location?.city ||
      (this.state && this.state !== location?.state) ||
      this.zipcode !== location?.zipcode
    ) {
      let updatedLocation = {
        ...location,
        street: this.streetAddress,
        city: this.city,
        zipcode: this.zipcode,
      };
      if (this.state) {
        updatedLocation = { ...updatedLocation, state: this.state };
      }
      this.updateUser({ ...this.user, location: updatedLocation }, true);
    }
    this.editAddressToggle = !this.editAddressToggle;
  }

  saveCCInfo() {
    if (
      this.ccName1 !== this.user.ccName1 ||
      this.ccName2 !== this.user.ccName2 ||
      this.ccEmail1 !== this.user.ccEmail1 ||
      this.ccEmail2 !== this.user.ccEmail2 ||
      this.ccPhone1 !== this.user.ccPhone1 ||
      this.ccPhone2 !== this.user.ccPhone2
    ) {
      this.updateUser(
        {
          ...this.user,
          ccName1: this.ccName1,
          ccName2: this.ccName2,
          ccEmail1: this.ccEmail1,
          ccEmail2: this.ccEmail2,
          ccPhone1: this.ccPhone1,
          ccPhone2: this.ccPhone2,
        },
        true,
      );
    }
    this.editCcInfoToggle = !this.editCcInfoToggle;
  }

  saveProfileAdditionalInfo() {
    if (
      this.bio !== this.user.bio ||
      this.timezone.id !== this.user.timezone?.id
    ) {
      this.updateUser(
        { ...this.user, bio: this.bio, timezone: this.timezone },
        true,
      );
    }
    this.editProfileAdditionalInfoToggle =
      !this.editProfileAdditionalInfoToggle;
  }

  saveOrgInfo() {
    if (
      this.orgName !== this.user.organization?.name ||
      this.orgEmail !== this.user.organization?.email ||
      this.orgPhone !== this.user.organization?.phone
    ) {
      const updatedOrg = {
        ...this.user.organization,
        name: this.orgName,
        email: this.orgEmail,
        phone: this.orgPhone,
      };
      this.updateUser({ ...this.user, organization: updatedOrg }, true);
    }
    this.editOrgInfoToggle = !this.editOrgInfoToggle;
  }

  saveOrgAddress() {
    const orgLocation = this.user.organization?.location;
    if (
      !orgLocation ||
      this.orgStreetAddress !== orgLocation?.street ||
      this.orgCity !== orgLocation?.city ||
      (this.orgState && this.orgState !== orgLocation?.state) ||
      this.orgZipcode !== orgLocation?.zipcode
    ) {
      let updatedLocation = {
        ...orgLocation,
        street: this.orgStreetAddress,
        city: this.orgCity,
        zipcode: this.orgZipcode,
      };
      if (this.orgState) {
        updatedLocation = { ...updatedLocation, state: this.orgState };
      }
      const updatedOrg = {
        ...this.user.organization,
        location: updatedLocation,
      };
      this.updateUser({ ...this.user, organization: updatedOrg }, true);
    }
    this.editOrgAddressToggle = !this.editOrgAddressToggle;
  }

  saveOrgAdditionalInfo() {
    if (
      this.orgDescription !== this.user.organization?.description ||
      this.orgWebsite !== this.user.organization?.website
    ) {
      const updatedOrg = {
        ...this.user.organization,
        description: this.orgDescription,
        website: this.orgWebsite,
      };
      this.updateUser({ ...this.user, organization: updatedOrg }, false);
    }
    this.editOrgAdditionalInfoToggle = !this.editOrgAdditionalInfoToggle;
  }

  saveServiceTypeCheckboxChange(serviceType: any) {
    let deleted = false;
    if (serviceType.checked) {
      this.newZoneServiceTypes = this.newZoneServiceTypes = [
        ...this.newZoneServiceTypes,
        { ...serviceType, enabledForZone: false, serviceSubTypes: [] },
      ];
      this.serviceTypes = this.serviceTypes.map((st: any) => {
        if (st.type === serviceType.type) {
          return {
            ...st,
            showSubTypes: serviceType.serviceSubTypes?.length > 0,
          };
        } else {
          return st;
        }
      });
    } else {
      // user is removing service type
      deleted = true;
      this.newZoneServiceTypes = this.newZoneServiceTypes?.filter(
        (currentServiceType: any) =>
          currentServiceType.type !== serviceType.type,
      );
      this.serviceTypes = this.serviceTypes.map((s: any) => {
        if (serviceType.type === s.type) {
          let serviceSubTypes = s.serviceSubTypes;
          if (serviceSubTypes) {
            serviceSubTypes = serviceSubTypes.map((sst: any) => {
              return {
                ...sst,
                checked: false,
              };
            });
          }
          return {
            ...s,
            checked: false,
            showSubTypes: false,
            serviceSubTypes: serviceSubTypes,
          };
        }
        return s;
      });
    }
    this.userService
      .updateServiceCapability(deleted, this.user, serviceType.type)
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedUser = response.data;
          if (updatedUser) {
            this.user = { ...this.user, ...updatedUser };
            localStorage.setItem('user', JSON.stringify(this.user));
            this.userService.updateUserSubject(this.user);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }

  saveSubServiceTypeCheckBoxChange(serviceType: any, serviceSubType: any) {
    let deleted = false;
    if (serviceSubType.checked) {
      this.newZoneServiceTypes = this.newZoneServiceTypes.map((st: any) => {
        if (st.type === serviceType.type) {
          if (st.serviceSubTypes?.length > 0) {
            return {
              ...st,
              serviceSubTypes: [
                ...st.serviceSubTypes,
                { ...serviceSubType, enabledForZone: false },
              ],
            };
          } else {
            return {
              ...st,
              serviceSubTypes: [{ ...serviceSubType, enabledForZone: false }],
            };
          }
        } else {
          return st;
        }
      });
    } else {
      deleted = true;
      this.newZoneServiceTypes = this.newZoneServiceTypes.map((st: any) => {
        if (st.type === serviceType.type) {
          return {
            ...st,
            serviceSubTypes: st.serviceSubTypes?.filter(
              (sst: any) => sst.type !== serviceSubType.type,
            ),
          };
        } else {
          return st;
        }
      });
    }
    this.userService
      .updateServiceSubType(
        deleted,
        this.user,
        serviceType.type,
        serviceSubType,
      )
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedUser = response.data;
          if (updatedUser) {
            this.user = { ...this.user, ...updatedUser };
            localStorage.setItem('user', JSON.stringify(this.user));
            this.userService.updateUserSubject(this.user);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }

  handleSelectAllNewZoneServiceTypes() {
    this.newZoneServiceTypes = this.newZoneServiceTypes.map(
      (currentServiceType: any) => {
        return {
          ...currentServiceType,
          enabledForZone: this.allNewZoneServiceTypesSelected,
          serviceSubTypes: currentServiceType.serviceSubTypes?.map(
            (subType: any) => {
              return {
                ...subType,
                enabledForZone: this.allNewZoneServiceTypesSelected,
              };
            },
          ),
        };
      },
    );
  }

  handleSelectAllUpdateZoneServiceTypes() {
    const updatedServices = this.currentServiceZone?.selectedServices?.map(
      (currentServiceType: any) => {
        return {
          ...currentServiceType,
          enabledForZone: this.allUpdateZoneServiceTypesSelected,
          serviceSubTypes: currentServiceType.serviceSubTypes?.map(
            (subType: any) => {
              return {
                ...subType,
                enabledForZone: this.allUpdateZoneServiceTypesSelected,
              };
            },
          ),
        };
      },
    );
    this.currentServiceZone = {
      ...this.currentServiceZone,
      selectedServices: updatedServices,
    };
  }

  handleNewZoneServiceTypeCheckboxChange() {
    this.allNewZoneServiceTypesSelected = this.newZoneServiceTypes.every(
      (serviceType: any) => serviceType.enabledForZone,
    );
  }

  handleUpdateZoneServiceTypeCheckboxChange() {
    this.allUpdateZoneServiceTypesSelected =
      this.currentServiceZone.selectedServices.every(
        (serviceType: any) =>
          serviceType.enabledForZone &&
          (!serviceType.serviceSubTypes ||
            serviceType.serviceSubTypes?.length === 0 ||
            serviceType.serviceSubTypes?.every(
              (subType: any) => subType.enabledForZone,
            )),
      );
  }

  handleNewZoneStateChange() {
    this.serviceZoneCounties = this.selectedNewZoneState.counties;
    this.selectedNewCounties = this.serviceZoneCounties?.includes('All Counties') ? ['All Counties'] : [];
  }

  handleUpdateZoneStateChange() {
    this.currentServiceZone = {
      ...this.currentServiceZone,
      selectedCounties: [],
    };
  }

  isOptionDisabled(counties: any, option: string) {
    return (
      (counties.some((county: string) => county === 'All Counties') &&
        option !== 'All Counties') ||
      (counties.some((county: string) => county !== 'All Counties') &&
        option === 'All Counties')
    );
  }

  saveNewServiceZone() {
    if (!this.selectedNewZoneState || !this.selectedNewZoneState.state) {
      this.toastr.error('Please select a state.');
      return;
    }
    if (
      !this.newZoneServiceTypes.some(
        (serviceType: any) => serviceType.enabledForZone === true,
      )
    ) {
      this.toastr.error('Please select at least one service.');
    } else if (
      this.selectedNewZoneState.state !== 'NATIONWIDE' &&
      this.selectedNewCounties?.length === 0
    ) {
      this.toastr.error('Please select at least one county.');
    } else if (
      this.user?.serviceZones?.some(
        (serviceZone: any) =>
          serviceZone.state === this.selectedNewZoneState.state,
      )
    ) {
      this.toastr.error('A service zone already exists for this state.');
    } else if (
      this.selectedNewZoneState.state === 'NATIONWIDE' &&
      this.user?.serviceZones?.some(
        (serviceZone: any) => serviceZone.nationwide,
      )
    ) {
      this.toastr.error('A nationwide service zone already exists.');
    } else {
      let newServiceZone = {};
      if (this.selectedNewZoneState.state === 'NATIONWIDE') {
        newServiceZone = {
          nationwide: true,
          services: this.newZoneServiceTypes,
        };
      } else {
        newServiceZone = {
          state: this.selectedNewZoneState.state,
          counties: this.selectedNewCounties,
          services: this.newZoneServiceTypes,
        };
      }
      this.userService
        .addNewServiceZone(this.user, newServiceZone)
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
      this.toggleNewServiceZone = false;
      this.resetNewServiceZoneValues();
    }
  }

  saveUpdatedServiceZone() {
    if (
      this.currentServiceZone.selectedState.state === 'NATIONWIDE' &&
      this.currentServiceZone.state !== 'NATIONWIDE' &&
      this.user.serviceZones?.some((serviceZone: any) => serviceZone.nationwide)
    ) {
      this.toastr.error('A nationwide service zone already exists.');
    } else if (
      this.currentServiceZone.selectedState.state !==
        this.currentServiceZone.state &&
      this.user.serviceZones?.some(
        (serviceZone: any) =>
          serviceZone.state === this.currentServiceZone.selectedState.state,
      )
    ) {
      this.toastr.error('A service zone already exists for this state.');
    } else if (
      this.currentServiceZone.selectedState.state !== 'NATIONWIDE' &&
      this.currentServiceZone.selectedCounties?.length === 0
    ) {
      this.toastr.error('Please select at least one county');
    } else if (
      !this.currentServiceZone.selectedServices?.some(
        (service: any) => service.enabledForZone,
      )
    ) {
      this.toastr.error('Please select at least one service');
    } else {
      let updatedServiceZone = {};
      if (this.currentServiceZone.selectedState.state === 'NATIONWIDE') {
        updatedServiceZone = {
          nationwide: true,
          services: this.currentServiceZone.selectedServices,
          zoneDocuments: this.currentServiceZone.zoneDocuments,
          serviceSubTypes: this.currentServiceZone.serviceSubTypes,
        };
      } else {
        updatedServiceZone = {
          state: this.currentServiceZone.selectedState.state,
          counties: this.currentServiceZone.selectedCounties,
          services: this.currentServiceZone.selectedServices,
          zoneDocuments: this.currentServiceZone.zoneDocuments,
          serviceSubTypes: this.currentServiceZone.serviceSubTypes,
        };
      }
      this.userService
        .updateServiceZone(
          this.user,
          updatedServiceZone,
          this.currentServiceZone.state,
        )
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
      this.editZoneToggle = !this.editZoneToggle;
      this.resetUpdateServiceZoneValues();
    }
  }

  removeServiceZone(serviceZone: any) {
    if (this.user.id) {
      let serviceZoneIdentifier;
      if (serviceZone.nationwide) {
        serviceZoneIdentifier = 'NATIONWIDE';
      } else {
        serviceZoneIdentifier = serviceZone.state;
      }
      this.userService
        .removeServiceZone(this.user.id, serviceZoneIdentifier)
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  cancelNewServiceZone() {
    this.toggleNewServiceZone = false;
    this.resetNewServiceZoneValues();
  }

  cancelUpdateServiceZone() {
    this.editZoneToggle = !this.editZoneToggle;
    this.resetUpdateServiceZoneValues();
  }

  uploadProfileDocument(event: any) {
    if (event.target.files) {
      this.toastr.info('Uploading profile documents...');
      this.documentService
        .uploadProfileDocuments(this.user, event.target.files)
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  uploadZoneDocument(serviceZone: any, event: any) {
    if (event.target.files) {
      this.toastr.info('Uploading service zone documents...');
      this.documentService
        .uploadServiceZoneDocuments(this.user, serviceZone, event.target.files)
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  deleteProfileDocument(documentId: string) {
    if (this.user.id) {
      this.documentService
        .deleteProfileDocument(this.user.id, documentId)
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  deleteZoneDocument(serviceZone: any, documentId: string) {
    if (this.user.id) {
      this.documentService
        .deleteZoneDocument(
          this.user.id,
          documentId,
          serviceZone.nationwide ? 'NATIONWIDE' : serviceZone.state,
        )
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  downloadDocument(document: any) {
    this.toastr.info('Downloading document...');
    this.documentService.downloadDocument(document);
  }

  uploadProfilePic(event: any) {
    if (event.target.files && event.target.files[0]) {
      this.userService
        .uploadProfilePicture(this.user, event.target.files[0])
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser && updatedUser.photo) {
              this.user = { ...this.user, photo: updatedUser.photo };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
              this.userService.updateUserSubject(this.user);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  verifyAddress(): void {
    if (this.verifyAddressDisabled) {
      return;
    }
    const profileSaveGeneration = profileSaves.generation;
    this.verificationPending = true;
    this.subscriptions.add(this.userService.verifyAddress().subscribe({
      next: (response) => {
        this.verificationPending = false;
        if (profileSaveGeneration !== profileSaves.generation) {
          this.toastr.info('Your profile changed while we were checking your address. Try again.');
          return;
        }
        if (!response?.data) {
          this.toastr.error('An error occurred. Please try again.');
          return;
        }
        this.storeVerificationFlag(response.data.addressVerificationNeeded);
        if (response.status === 'SUCCESS') {
          this.toastr.success(response.message || 'Address verified.');
        } else {
          this.toastr.error(response.message || 'An error occurred. Please try again.');
          if (response.message !== 'Your address changed while we were checking it. Try again.') {
            this.verificationCooldown = true;
            this.subscriptions.add(timer(30000).subscribe(() => {
              this.verificationCooldown = false;
            }));
          }
        }
      },
      error: () => {
        this.verificationPending = false;
        this.toastr.error('An error occurred. Please try again.');
      },
    }));
  }

  private storeUpdatedUser(updatedUser: User): void {
    this.user = { ...this.user, ...updatedUser };
    setStoredUser(this.user);
  }

  // Merged into the latest stored user, not this page's copy: a save sent from a Manage Account closed since this one
  // opened may have stored a newer profile, which writing this page's copy back would undo.
  private storeVerificationFlag(addressVerificationNeeded: boolean | undefined): void {
    this.user = { ...this.user, ...getStoredUser(), addressVerificationNeeded };
    setStoredUser(this.user);
  }

  private updateUser(user: User, displayMessge: boolean) {
    profileSaves.generation++;
    profileSaves.pending++;
    this.userService.updateUser(user).pipe(
      finalize(() => profileSaves.pending--),
    ).subscribe((response: any) => {
      const apiResponse = response;
      if (apiResponse.status === 'FAILURE') {
        if (apiResponse.message) {
          this.toastr.error(apiResponse.message);
        } else {
          this.toastr.error('An error occurred. Please try again.');
        }
      } else {
        const updatedUser = response.data;
        if (updatedUser) {
          this.storeUpdatedUser(updatedUser);
          if (displayMessge) {
            this.toastr.success(apiResponse.message);
          }
        } else {
          this.toastr.error('An error occurred. Please try again.');
        }
      }
    });
  }

  private resetNewServiceZoneValues() {
    this.selectedNewZoneState = '';
    this.selectedNewCounties = [];
    this.newZoneServiceTypes = this.newZoneServiceTypes.map(
      (currentServiceType: any) => {
        return { ...currentServiceType, enabledForZone: false };
      },
    );
    this.allNewZoneServiceTypesSelected = false;
  }

  private resetUpdateServiceZoneValues() {
    this.currentServiceZone = {};
    this.allUpdateZoneServiceTypesSelected = false;
  }
}
